/*
 * KanAI Thinking Orb — rendermotor (canvas, 2D).
 *
 * Trogen port av thinking-orbs (MIT (c) Jakub Antalik,
 * https://github.com/Jakubantalik/thinking-orbs). TS -> JS per ADR 0005.
 * Enda funktionella andringen mot originalet: paint() skriver KanAI-lila
 * i stallet for grayscala (samma ink/djup-varden styr ljushet).
 *
 * Ren 2D-canvas (inga filter/WebGL) -> billigt aven pa faltenheter.
 * Ingen React har inne; ThinkingOrb.jsx driver canvas + RAF.
 */

// ── core ──────────────────────────────────────────────────────────────
const hashD = (a, b) => { const h = Math.sin(a * 12.9898 + b * 78.233) * 43758.5453; return h - Math.floor(h); };
function fibDir(i, n) { const g = Math.PI * (3 - Math.sqrt(5)); const y = 1 - (2 * (i + 0.5)) / n; const rad = Math.sqrt(1 - y * y); const a = i * g; return [rad * Math.cos(a), y, rad * Math.sin(a)]; }
const angleDelta = (a, b) => Math.atan2(Math.sin(a - b), Math.cos(a - b));
function makeProj(yaw, tilt, cx, cy, scale) {
  const st = Math.sin(tilt), ct = Math.cos(tilt), sy = Math.sin(yaw), cyw = Math.cos(yaw);
  return (x, y, z) => { const x1 = x * cyw + z * sy; const z1 = -x * sy + z * cyw; const y1 = y * ct - z1 * st; const z2 = y * st + z1 * ct; return [cx + x1 * scale, cy - y1 * scale, z2]; };
}
const radiusScale = (size, pow) => Math.pow(size / 300, pow);

// ── farg-painter: KanAI-lila (ink-vardet styr ljushet, som originalet) ─
const LIGHT = { far: [210, 199, 252], near: [84, 28, 168] };
const DARK = { far: [99, 70, 158], near: [203, 191, 253] };
const lp = (a, b, t) => a + (b - a) * t;

// render-installningar satta av drawOrb precis fore varje mode.draw
let _dark = false, _shape = 'round', _mul = 1;

function paint(ctx, dots, _dk, rMin = 0.3) {
  dots.sort((a, b) => a.z - b.z);
  const P = _dark ? DARK : LIGHT;
  for (const d of dots) {
    const alpha = d.a == null ? 1 : d.a;
    if (alpha < 0.02) continue;
    const w = Math.min(1, Math.max(0, d.white)), n = 1 - w;
    const cr = lp(P.far[0], P.near[0], n) | 0, cg = lp(P.far[1], P.near[1], n) | 0, cb = lp(P.far[2], P.near[2], n) | 0;
    ctx.fillStyle = 'rgba(' + cr + ',' + cg + ',' + cb + ',' + alpha + ')';
    const rr = Math.max(rMin, d.r) * _mul;
    if (_shape === 'square') { const s = rr * 1.8; ctx.fillRect(d.x - s / 2, d.y - s / 2, s, s); }
    else { ctx.beginPath(); ctx.arc(d.x, d.y, rr, 0, Math.PI * 2); ctx.fill(); }
  }
}

// ── orbits (working) ──────────────────────────────────────────────────
function drawOrbits(ctx, size, t, dk, o) {
  const cx = size / 2, cy = size / 2, R = (size / 2) * 0.82;
  const pt = makeProj(t * 0.12, 0.3, cx, cy, 1), rs = radiusScale(size, o.rsPow ?? 0.6);
  const dots = [], orbitN = o.orbitN ?? 12, ghostN = o.ghostN ?? 40, particles = o.particles ?? 3;
  for (let orb = 0; orb < orbitN; orb++) {
    const h1 = hashD(orb, 1.7), h2 = hashD(orb, 5.2), h3 = hashD(orb, 8.9);
    const ro = R * (0.45 + 0.52 * h1), th = h1 * 2 * Math.PI, phi = Math.acos(2 * h2 - 1);
    const nx = Math.sin(phi) * Math.cos(th), ny = Math.cos(phi), nz = Math.sin(phi) * Math.sin(th);
    let ux = -ny, uy = nx; const uz = 0; const ul = Math.max(1e-6, Math.sqrt(ux * ux + uy * uy)); ux /= ul; uy /= ul;
    const vx = ny * uz - nz * uy, vy = nz * ux - nx * uz, vz = nx * uy - ny * ux;
    const speed = (0.25 + 0.55 * h3) * (h3 > 0.5 ? 1 : -1);
    for (let k = 0; k < ghostN; k++) {
      const a = (k / ghostN) * 2 * Math.PI;
      const p = pt((ux * Math.cos(a) + vx * Math.sin(a)) * ro, (uy * Math.cos(a) + vy * Math.sin(a)) * ro, (uz * Math.cos(a) + vz * Math.sin(a)) * ro);
      const depth = (p[2] / ro + 1) / 2;
      dots.push({ x: p[0], y: p[1], z: p[2], r: (o.ghostR ?? 0.9) * rs, white: 0.72, a: (o.ghostA ?? 0.5) * (0.4 + 0.6 * depth) });
    }
    for (let m = 0; m < particles; m++) {
      const a = t * speed + (m / particles) * 2 * Math.PI + h2 * 6;
      const p = pt((ux * Math.cos(a) + vx * Math.sin(a)) * ro, (uy * Math.cos(a) + vy * Math.sin(a)) * ro, (uz * Math.cos(a) + vz * Math.sin(a)) * ro);
      const depth = (p[2] / ro + 1) / 2;
      dots.push({ x: p[0], y: p[1], z: p[2], r: ((o.partR ?? 1.2) + (o.partRDepth ?? 1.6) * depth) * rs, white: 0.3 - 0.22 * depth });
    }
  }
  paint(ctx, dots, dk, o.rMin);
}

// ── globe (searching) ─────────────────────────────────────────────────
function drawGlobe(ctx, size, t, dk, o) {
  const spin = 0.5, cx = size / 2, cy = size / 2, radius = (size / 2) * 0.82;
  const tilt = 0.4 + 0.06 * Math.sin(t * 0.35), pt = makeProj(t * spin, tilt, cx, cy, radius);
  const scan = t * (spin + (1.7 - spin) * (o.scanMul ?? 1)), rs = radiusScale(size, o.rsPow ?? 0.6), dimBase = o.dimBase ?? 1;
  const dots = [], latRings = o.latRings ?? 17, lonDensity = o.lonDensity ?? 44;
  for (let li = 0; li <= latRings; li++) {
    const lat = -Math.PI / 2 + (li / latRings) * Math.PI, cosLat = Math.cos(lat), sinLat = Math.sin(lat);
    const lonCount = Math.max(1, Math.round(Math.abs(cosLat) * lonDensity));
    for (let lj = 0; lj < lonCount; lj++) {
      const lon = (lj / lonCount) * 2 * Math.PI;
      const p = pt(cosLat * Math.cos(lon), sinLat, cosLat * Math.sin(lon)), depth = (p[2] + 1) / 2;
      const d = angleDelta(lon + t * spin, scan), boost = Math.exp(-(d * d) / 0.18) * Math.max(0, p[2]);
      dots.push({ x: p[0], y: p[1], z: p[2], r: ((o.rBase ?? 0.6) + (o.rDepth ?? 1.7) * depth + (o.rBoost ?? 1) * boost) * rs, white: (o.inkFar ?? 0.62) - (o.inkSpan ?? 0.54) * depth, a: dimBase + (1 - dimBase) * Math.min(1, boost) });
    }
  }
  paint(ctx, dots, dk, o.rMin);
}

// ── rubik (solving) ───────────────────────────────────────────────────
function solveCycle(time, count, slotDur, rest) {
  const cyc = 2 * count * slotDur + rest, tc = time % cyc, amount = new Array(count).fill(0); let active = -1;
  if (tc < 2 * count * slotDur) {
    const slot = Math.floor(tc / slotDur), p = (tc - slot * slotDur) / slotDur, cl = Math.min(1, p / 0.7), ep = 1 - Math.pow(1 - cl, 3);
    if (slot < count) { for (let i = 0; i < slot; i++) amount[i] = 1; amount[slot] = ep; active = slot; }
    else { const u = 2 * count - 1 - slot; for (let i = 0; i < u; i++) amount[i] = 1; amount[u] = 1 - ep; active = u; }
  }
  return { amount, active };
}
function makeMoves(count) {
  const moves = [];
  for (let i = 0; i < count; i++) {
    const axis = Math.min(2, Math.floor(hashD(i, 2.3) * 3)), lo = -1.0 + 0.5 * Math.min(3, Math.floor(hashD(i, 5.9) * 4)), dir = hashD(i, 7.7) < 0.5 ? 1 : -1;
    moves.push({ axis, lo, hi: lo + 0.5, ang: dir * Math.PI / 2 });
  }
  return moves;
}
function applyMoves(pt3, moves, sc) {
  let x = pt3[0], y = pt3[1], z = pt3[2], inActive = false;
  for (let i = 0; i < moves.length; i++) {
    if (sc.amount[i] <= 0) continue;
    const mv = moves[i], coord = mv.axis === 0 ? x : mv.axis === 1 ? y : z;
    if (coord < mv.lo || coord >= mv.hi) continue;
    if (i === sc.active) inActive = true;
    const a = mv.ang * sc.amount[i], ca = Math.cos(a), sa = Math.sin(a);
    if (mv.axis === 0) { const y2 = y * ca - z * sa; z = y * sa + z * ca; y = y2; }
    else if (mv.axis === 1) { const x2 = x * ca + z * sa; z = -x * sa + z * ca; x = x2; }
    else { const x2 = x * ca - y * sa; y = x * sa + y * ca; x = x2; }
  }
  return [x, y, z, inActive];
}
function drawRubik(ctx, size, t, dk, o) {
  const cx = size / 2, cy = size / 2, R = (size / 2) * 0.82;
  const pt = makeProj(t * 0.55, 0.35 + 0.1 * Math.sin(t * 0.9), cx, cy, R), rs = radiusScale(size, o.rsPow ?? 0.6);
  const moves = makeMoves(o.moveCount ?? 14), sc = solveCycle(t, o.moveCount ?? 14, 0.42, 1.2);
  const dots = [], latRings = o.latRings ?? 15, lonDensity = o.lonDensity ?? 40;
  for (let li = 0; li <= latRings; li++) {
    const lat = -Math.PI / 2 + (li / latRings) * Math.PI, cosLat = Math.cos(lat), sinLat = Math.sin(lat);
    const lonCount = Math.max(1, Math.round(Math.abs(cosLat) * lonDensity));
    for (let lj = 0; lj < lonCount; lj++) {
      const lon = (lj / lonCount) * 2 * Math.PI;
      const m = applyMoves([cosLat * Math.cos(lon), sinLat, cosLat * Math.sin(lon)], moves, sc), p = pt(m[0], m[1], m[2]), depth = (p[2] + 1) / 2;
      dots.push({ x: p[0], y: p[1], z: p[2], r: ((o.rBase ?? 0.6) + (o.rDepth ?? 1.7) * depth + (m[3] ? (o.rActive ?? 0.3) : 0)) * rs, white: (o.inkFar ?? 0.62) - (o.inkSpan ?? 0.54) * depth - (m[3] ? 0.14 : 0) });
    }
  }
  paint(ctx, dots, dk, o.rMin);
}

// ── wave (listening) ──────────────────────────────────────────────────
function drawWave(ctx, size, t, dk, o) {
  const cx = size / 2, cy = size / 2, R = (size / 2) * 0.874, pt = makeProj(t * 0.18, 0.38, cx, cy, 1), rs = radiusScale(size, o.rsPow ?? 0.6);
  const dots = [], rings = o.rings ?? 15, lonDensity = o.lonDensity ?? 40;
  for (let ri = 0; ri <= rings; ri++) {
    const lat = -Math.PI / 2 + (ri / rings) * Math.PI, cosLat = Math.cos(lat), sinLat = Math.sin(lat);
    const w = 0.62 * Math.sin(t * 2.1 - ri * 0.52) + 0.38 * Math.sin(t * 1.27 + ri * 0.83), rr = R * (0.88 + 0.105 * w);
    const lonCount = Math.max(1, Math.round(Math.abs(cosLat) * lonDensity));
    for (let lj = 0; lj < lonCount; lj++) {
      const lon = (lj / lonCount) * 2 * Math.PI, p = pt(cosLat * Math.cos(lon) * rr, sinLat * rr, cosLat * Math.sin(lon) * rr);
      const depth = (p[2] / R + 1) / 2, crest = Math.max(0, w);
      dots.push({ x: p[0], y: p[1], z: p[2], r: ((o.rBase ?? 0.6) + (o.rDepth ?? 1.7) * depth) * (1 + 0.4 * crest) * rs, white: 0.66 - 0.56 * depth - 0.1 * crest });
    }
  }
  paint(ctx, dots, dk, o.rMin);
}

// ── ribbon (composing) ────────────────────────────────────────────────
function drawRibbon(ctx, size, t, dk, o) {
  const cx = size / 2, cy = size / 2, R = (size / 2) * 0.78, spin = o.spin ?? 1, pt = makeProj(t * 0.1 * spin, 0.3, cx, cy, 1), rs = radiusScale(size, o.rsPow ?? 0.6);
  const dots = [], ghostN = o.ghostN ?? 150;
  for (let i = 0; i < ghostN; i++) { const d = fibDir(i, ghostN), p = pt(d[0] * R, d[1] * R, d[2] * R), depth = (p[2] / R + 1) / 2; dots.push({ x: p[0], y: p[1], z: p[2], r: 0.8 * rs, white: 0.78, a: 0.1 + 0.22 * depth }); }
  const ya = t * 0.24 * spin, ta = 0.55 + 0.3 * Math.sin(t * 0.18) * spin;
  const ux = Math.cos(ya), uy = 0, uz = Math.sin(ya), vx = -uz * Math.sin(ta), vy = Math.cos(ta), vz = ux * Math.sin(ta);
  const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
  const baseLanes = o.lanes ?? 5, segs = o.segs ?? 88, lanes = Math.max(1, Math.round(baseLanes * (o.bandMul ?? 1)));
  for (let w = 0; w < lanes; w++) {
    const laneOff = (w - (lanes - 1) / 2) * 0.075, edge = Math.abs(w - (lanes - 1) / 2) / Math.max(1, (lanes - 1) / 2);
    for (let k = 0; k < segs; k++) {
      const a = (k / segs) * 2 * Math.PI;
      const wob = (0.16 * Math.sin(a * 3 - t * 1.7 + w * 0.22) + 0.07 * Math.sin(a * 5 + t * 1.1)) * (o.wobMul ?? 1), off = laneOff + wob;
      const x = ux * Math.cos(a) + vx * Math.sin(a) + nx * off, y = uy * Math.cos(a) + vy * Math.sin(a) + ny * off, z = uz * Math.cos(a) + vz * Math.sin(a) + nz * off;
      const l = Math.sqrt(x * x + y * y + z * z), p = pt((x / l) * R, (y / l) * R, (z / l) * R), depth = (p[2] / R + 1) / 2;
      dots.push({ x: p[0], y: p[1], z: p[2], r: ((o.rBase ?? 1.1) + (o.rDepth ?? 1.7) * depth) * (1 - 0.25 * edge) * rs, white: 0.52 - 0.44 * depth + 0.18 * edge, a: 0.4 + 0.6 * depth });
    }
  }
  paint(ctx, dots, dk, o.rMin);
}

// ── morph (shaping) ───────────────────────────────────────────────────
const smoothE = x => x * x * (3 - 2 * x);
function polyPath(verts) {
  const V = verts.length, L = []; let total = 0;
  for (let i = 0; i < V; i++) { const a = verts[i], b = verts[(i + 1) % V], l = Math.hypot(b[0] - a[0], b[1] - a[1]); L.push(l); total += l; }
  return f => { let target = f * total, i = 0; while (target > L[i] && i < V - 1) { target -= L[i]; i++; } const a = verts[i], b = verts[(i + 1) % V], ff = L[i] ? Math.min(1, target / L[i]) : 0; return [a[0] + (b[0] - a[0]) * ff, a[1] + (b[1] - a[1]) * ff]; };
}
const CIRCLE = f => { const a = -Math.PI / 2 + f * 2 * Math.PI; return [Math.cos(a) * 0.24, Math.sin(a) * 0.24]; };
const TRIANGLE = polyPath([[0.0, -0.26], [0.24, 0.16], [-0.24, 0.16]]);
const SQUARE = polyPath([[0, -0.2], [0.2, -0.2], [0.2, 0.2], [-0.2, 0.2], [-0.2, -0.2]]);
const CYCLE = [CIRCLE, TRIANGLE, SQUARE];
const morphN = d => Math.max(6, Math.round(34 * d));
const HOLD = 1.4, MORPH = 0.9, SEG = HOLD + MORPH;
function drawMorph(ctx, size, t, dk, o) {
  const K = CYCLE.length, tc = t % (SEG * K), k = Math.floor(tc / SEG), local = tc - k * SEG, m = local > HOLD ? smoothE((local - HOLD) / MORPH) : 0, sprd = o.spread ?? 1;
  const pA = CYCLE[k], pB = CYCLE[(k + 1) % K], M = 160, pts = [];
  for (let i = 0; i < M; i++) { const f = i / M, a = pA(f), b = pB(f); pts.push([(a[0] + (b[0] - a[0]) * m) * sprd, (a[1] + (b[1] - a[1]) * m) * sprd]); }
  const L = []; let total = 0;
  for (let i = 0; i < M; i++) { const a = pts[i], b = pts[(i + 1) % M], l = Math.hypot(b[0] - a[0], b[1] - a[1]); L.push(l); total += l; }
  const n = morphN(o.iconD ?? 1), re = (o.rDot ?? 0.021) * 1.35 * sprd, pulse = 1 + 0.02 * Math.sin(local * 3.1);
  const dots = [], c2 = size / 2; let seg = 0, acc = 0;
  for (let k2 = 0; k2 < n; k2++) {
    const target = (k2 / n) * total;
    while (acc + L[seg] < target && seg < M - 1) { acc += L[seg]; seg++; }
    const a = pts[seg], b = pts[(seg + 1) % M], f = L[seg] ? Math.min(1, (target - acc) / L[seg]) : 0;
    const x = (a[0] + (b[0] - a[0]) * f) * pulse, y = (a[1] + (b[1] - a[1]) * f) * pulse;
    dots.push({ x: c2 + x * size, y: c2 + y * size, z: 0, r: Math.max(0.35, re * size), white: 0.1 });
  }
  paint(ctx, dots, dk, o.rMin);
}

// ── preset-tuningar (fran originalets presets.ts; small = 20px, large = 64px) ──
const MODES = {
  orbits: { draw: drawOrbits,
    small: { speed: 3.9, opts: { orbitN: 3, ghostN: 10, ghostR: 2.16, ghostA: 0.5, particles: 3, partR: 2.88, partRDepth: 3.84, rsPow: 0.6, rMin: 0.3 } },
    large: { speed: 1.885, opts: { orbitN: 12, ghostN: 40, ghostR: 0.9, ghostA: 0.5, particles: 3, partR: 1.2, partRDepth: 1.6, rsPow: 0.6, rMin: 0.3 } } },
  globe: { draw: drawGlobe,
    small: { speed: 2.665, opts: { latRings: 6, lonDensity: 14, rBase: 1.05, rDepth: 2.975, rBoost: 1.0, inkFar: 0.62, inkSpan: 0.54, rsPow: 0.6, rMin: 0.3, scanMul: 4.335, dimBase: 0.45 } },
    large: { speed: 2.015, opts: { latRings: 11, lonDensity: 29, rBase: 0.69, rDepth: 1.955, rBoost: 1.0, inkFar: 0.62, inkSpan: 0.54, rsPow: 0.6, rMin: 0.3, scanMul: 4.08, dimBase: 0.45 } } },
  rubik: { draw: drawRubik,
    small: { speed: 1.95, opts: { latRings: 4, lonDensity: 12, moveCount: 14, rBase: 1.14, rDepth: 3.23, rActive: 0.57, inkFar: 0.62, inkSpan: 0.54, rsPow: 0.6, rMin: 0.3 } },
    large: { speed: 1.82, opts: { latRings: 9, lonDensity: 24, moveCount: 14, rBase: 0.63, rDepth: 1.785, rActive: 0.315, inkFar: 0.62, inkSpan: 0.54, rsPow: 0.6, rMin: 0.3 } } },
  wave: { draw: drawWave,
    small: { speed: 3.998, opts: { rings: 5, lonDensity: 13, rBase: 0.96, rDepth: 2.72, rsPow: 0.6, rMin: 0.3 } },
    large: { speed: 4.388, opts: { rings: 9, lonDensity: 23, rBase: 0.6, rDepth: 1.7, rsPow: 0.6, rMin: 0.3 } } },
  ribbon: { draw: drawRibbon,
    small: { speed: 3.12, opts: { lanes: 2, segs: 20, ghostN: 8, rBase: 1.18, rDepth: 1.824, rsPow: 0.6, rMin: 0.3, spin: 0, bandMul: 4.94, wobMul: 1 } },
    large: { speed: 2.34, opts: { lanes: 3, segs: 44, ghostN: 38, rBase: 0.935, rDepth: 1.445, rsPow: 0.6, rMin: 0.3, spin: 0, bandMul: 3.9, wobMul: 1 } } },
  morph: { draw: drawMorph,
    small: { speed: 2.08, opts: { rDot: 0.02123, iconD: 0.53, rMin: 0.25, spread: 1.45 } },
    large: { speed: 2.405, opts: { rDot: 0.008295, iconD: 0.54, rMin: 0.25, spread: 1.45 } } }
};

// KanAI-semantiska varianter -> mode-nyckel. Direkta mode-namn funkar ocksa.
export const VARIANT_TO_MODE = {
  thinking: 'rubik', reasoning: 'rubik', solving: 'rubik',
  searching: 'globe', 'tool-use': 'globe', tooluse: 'globe',
  streaming: 'ribbon', writing: 'ribbon', composing: 'ribbon',
  working: 'orbits', generating: 'orbits',
  listening: 'wave',
  shaping: 'morph', formatting: 'morph',
  orbits: 'orbits', globe: 'globe', rubik: 'rubik', wave: 'wave', ribbon: 'ribbon', morph: 'morph'
};

/**
 * Rita en orb-frame.
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} size  CSS-px (kvadratisk canvas)
 * @param {string} variant  KanAI-variant eller mode-nyckel
 * @param {number} tSeconds  forfluten tid i sekunder (RAF-klocka)
 * @param {{dark?:boolean, shape?:'round'|'square', mul?:number}} [render]
 */
export function drawOrb(ctx, size, variant, tSeconds, render) {
  const modeKey = VARIANT_TO_MODE[variant] || 'rubik';
  const mode = MODES[modeKey];
  const tune = size <= 40 ? mode.small : mode.large;
  _dark = !!(render && render.dark);
  _shape = (render && render.shape) || 'round';
  _mul = (render && render.mul) || 1;
  mode.draw(ctx, size, tSeconds * tune.speed, _dark, tune.opts);
}
